import { useTheme } from "@emotion/react";
import {
	Autocomplete,
	Button,
	TextField,
	Grid,
	useMediaQuery,
	InputAdornment,
	IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { renderCodeOption } from "../../utils/functions";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import {
	formatSaveDateFrom,
	formatSaveDateTo,
} from "../../reducers/TimeReducer";
import { Clear } from "@mui/icons-material";
import PrintsSearch3 from "../../components/PrintSearch3";
import PATClearButton from "../../components/Buttons/PATClearButton";

const headers = [
	{ key: "title", label: "Title" },
	{ key: "amount", label: "Amount", format: "currency" },
	{ key: "remaining", label: "Remaining", format: "currency" },
	{ key: "source", label: "Source" },
	{ key: "administrator", label: "Administrator" },
	{ key: "startDate", label: "Start Date", format: "date" },
	{ key: "endDate", label: "End Date", format: "date" },
	{ key: "status", label: "Status" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
	...h,
}));

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const FundingSourceTableToolbar = ({
	fundingSources,
	openAdd,
	search,
	setSearch,
	status,
	onFilterChange,
	filters,
	resetFilters,
	sourceOptions,
	administratorOptions,
	dispensableItems,
	vehicles,
	equipment,
	workOrders,
	maintenance,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
		"&:hover": {
			borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
			backgroundColor: theme.palette.action.hover,
		},
	};

	const [showAdvSearch, setShowAdvSearch] = useState(false);
	const [localFilters, setLocalFilters] = useState(filters);
	const [printFlag, setPrintFlag] = useState(false);

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	const calcRemaining = (f) => {
		const spentDisp = (dispensableItems || []).reduce((s, i) => {
			if (i.fundingSourceID !== f.id) return s;
			return (
				s +
				(i.issuances || [])
					.filter((iss) => iss.isRestock && iss.purchaseCost != null)
					.reduce((a, iss) => a + (iss.purchaseCost || 0), 0)
			);
		}, 0);
		const spent =
			(vehicles || [])
				.filter((v) => v.fundingSourceID === f.id)
				.reduce((s, v) => s + (v.originalCost || 0), 0) +
			(equipment || [])
				.filter((e) => e.fundingSourceID === f.id)
				.reduce((s, e) => s + (e.cost || 0), 0) +
			(workOrders || [])
				.filter((w) => w.fundingSourceID === f.id)
				.reduce((s, w) => s + (w.cost || 0), 0) +
			(maintenance || [])
				.filter((m) => m.fundingSourceID === f.id)
				.reduce((s, m) => s + (m.cost || 0), 0) +
			spentDisp;
		return (f.amount || 0) - spent;
	};

	const fundingSourcesWithRemaining = useMemo(
		() =>
			fundingSources.map((fs) => ({
				...fs,
				remaining: calcRemaining(fs),
			})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fundingSources],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const renderClearButton = () => {
		return (
			<>
				<Grid item>
					<Grid item>
						<PATClearButton
							title="Clear Filters"
							onClick={resetFilters}
						/>
					</Grid>
				</Grid>
			</>
		);
	};

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : type === "date" ? (
						<DatePicker
							label={label}
							value={
								localFilters[field]
									? moment(localFilters[field])
									: null
							}
							onChange={(date) =>
								handleFilterChange(
									field,
									date
										? field === "from"
											? formatSaveDateFrom(date)
											: formatSaveDateTo(date)
										: null,
								)
							}
							minDate={
								field === "to" && localFilters.from
									? moment(localFilters.from)
									: undefined
							}
							maxDate={
								field === "from" && localFilters.to
									? moment(localFilters.to)
									: undefined
							}
							clearable
							clearText="Clear"
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{localFilters[field] && (
													<InputAdornment position="end">
														<IconButton
															onClick={() =>
																handleFilterChange(
																	field,
																	null,
																)
															}
															size="small"
															edge="end"
														>
															<Clear fontSize="small" />
														</IconButton>
													</InputAdornment>
												)}
												{
													params.InputProps
														?.endAdornment
												}
											</>
										),
									}}
								/>
							)}
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: status,
			xs: 1,
		},
	];

	const advanceFilterFields = [
		{
			type: "autocomplete",
			label: "Source",
			field: "source",
			options: sourceOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Administrator",
			field: "administrator",
			options: administratorOptions,
			multiselect: true,
			xs: 1.7,
		},
	];

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Funding Sources</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search unit, employee..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
					}}
				/>
				{renderFilterFields(filterFields)}
				<Button
					variant="outlined"
					color="primary"
					sx={btnStyles}
					onClick={() => setShowAdvSearch((s) => !s)}
				>
					🔍 Advanced
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handlePrintClick}
					title="Print Funding Sources"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button variant="contained" color="primary" onClick={openAdd}>
					+ New Source
				</Button>
			</div>
			{showAdvSearch && (
				<div
					style={{
						margin: "8px",
						padding: "14px 16px",
						border: "1px solid var(--border)",
						borderRadius: "8px",
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						alignItems: "center",
					}}
				>
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Filter by:
					</span>
					{renderFilterFields([...advanceFilterFields])}
					{renderClearButton()}
				</div>
			)}

			<PrintsSearch3
				title="Funding Sources"
				data={fundingSourcesWithRemaining}
				cols={columnsForPrint}
				print={printFlag}
			/>
		</>
	);
};

export default FundingSourceTableToolbar;
